import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import StyledTableContainer from "@components/StyledTable";
import { Patient } from "@models/patient";
import PatientRow from "@components/PatientList/PatientRow";

export interface PatientListProps {
  patients: Patient[];
  selectedIds: Set<string>;
  onSelectionChange: (patientId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onEdit: (patient: Patient) => void;
  onToggleActive: (patient: Patient) => void;
  onArchiveToggle: (patient: Patient) => void;
  onOpenPortal: (patient: Patient) => void;
  onEditProfile: (patient: Patient) => void;
  isMutating?: boolean;
}

const PatientList: FC<PatientListProps> = ({
  patients,
  selectedIds,
  onSelectionChange,
  onSelectAll,
  onEdit,
  onToggleActive,
  onArchiveToggle,
  onOpenPortal,
  onEditProfile,
  isMutating,
}) => {
  const { t } = useTranslation();
  const allSelected =
    patients.length > 0 && patients.every((p) => selectedIds.has(p.id));
  const someSelected = patients.some((p) => selectedIds.has(p.id));

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelectAll(event.target.checked);
    },
    [onSelectAll],
  );

  return (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableContainer.StyledTableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                onChange={handleSelectAll}
                inputProps={{
                  "aria-label": t("patientManage.export.selectAll"),
                }}
              />
            </StyledTableContainer.StyledTableCell>
            <StyledTableContainer.StyledTableCell>
              {t("patientManage.columns.name")}
            </StyledTableContainer.StyledTableCell>
            <StyledTableContainer.StyledTableCell>
              {t("patientManage.columns.status")}
            </StyledTableContainer.StyledTableCell>
            <StyledTableContainer.StyledTableCell>
              {t("patientManage.columns.lastActivity")}
            </StyledTableContainer.StyledTableCell>
            <StyledTableContainer.StyledTableCell align="right">
              {t("patientManage.columns.actions")}
            </StyledTableContainer.StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map((patient) => (
            <PatientRow
              key={patient.id}
              patient={patient}
              selected={selectedIds.has(patient.id)}
              onSelect={(selected) => onSelectionChange(patient.id, selected)}
              onEdit={() => onEdit(patient)}
              onToggleActive={() => onToggleActive(patient)}
              onArchiveToggle={() => onArchiveToggle(patient)}
              onOpenPortal={() => onOpenPortal(patient)}
              onEditProfile={() => onEditProfile(patient)}
              isMutating={isMutating}
            />
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default PatientList;
